"use client";

import * as React from "react";
import { handleError } from "@hs/shared";
import { trpc } from "@/lib/trpc";
import { Button, Description, Modal, Spinner, toast } from "@heroui/react";
import { RacDialogTriggerAnchor } from '@/components/RacDialogTriggerAnchor';

const RESET_PASSWORD = "Abc@123";

export interface ResetPasswordDialogTarget {
  userId?: string;
  peopleId?: string;
  displayName: string;
  fullName?: string | null;
  jobNumber?: string | null;
  username?: string | null;
}

export interface ResetPasswordDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  target: ResetPasswordDialogTarget | null;
  onSuccess?: () => void;
}

export function ResetPasswordDialog({
  open,
  onOpenChange,
  target,
  onSuccess,
}: ResetPasswordDialogProps) {
  const [displayText, setDisplayText] = React.useState("");
  const hasMutatedRef = React.useRef<string | null>(null);

  const personQuery = trpc.people.get.useQuery(
    { id: target?.peopleId ?? "" },
    { enabled: open && !!target?.peopleId && !target?.userId }
  );

  const effectiveTarget: ResetPasswordDialogTarget | null =
    target?.peopleId && personQuery.data
      ? {
          userId: (personQuery.data as { user?: { id?: string } }).user?.id,
          displayName:
            (personQuery.data as { fullName?: string }).fullName ?? target.displayName,
          fullName: (personQuery.data as { fullName?: string }).fullName ?? target.fullName,
          jobNumber: (personQuery.data as { jobNumber?: string }).jobNumber ?? target.jobNumber,
          username: (personQuery.data as { user?: { username?: string } }).user?.username ?? target.username,
        }
      : target;

  const resetPasswordMutation = trpc.users.resetPassword.useMutation({
    onSuccess: () => {
      toast.success("密码已重置");
      onSuccess?.();
      if (effectiveTarget) {
        const fullName = effectiveTarget.fullName ?? effectiveTarget.displayName ?? "";
        const jobNumber = effectiveTarget.jobNumber ?? "";
        const username = effectiveTarget.username ?? "";
        setDisplayText(
          `亲爱的${fullName}（工号：${jobNumber}）,你的用户名是：${username}，新密码是：${RESET_PASSWORD}，请及时登录并变更密码。`
        );
      }
    },
    onError: (e) => {
      const info = handleError(e);
      toast.danger(info.message || "重置失败");
    },
  });

  React.useEffect(() => {
    if (!open || !effectiveTarget?.userId) return;
    if (hasMutatedRef.current === effectiveTarget.userId) return;
    hasMutatedRef.current = effectiveTarget.userId;
    resetPasswordMutation.mutate({ id: effectiveTarget.userId, newPassword: RESET_PASSWORD });
  }, [open, effectiveTarget, resetPasswordMutation]);

  React.useEffect(() => {
    if (!open) {
      hasMutatedRef.current = null;
      setDisplayText("");
    }
  }, [open]);

  const handleOpenChange = React.useCallback(
    (next: boolean) => {
      onOpenChange(next);
      if (!next) setDisplayText("");
    },
    [onOpenChange]
  );

  const handleCopy = React.useCallback(() => {
    if (!displayText) return;
    try {
      const selection = window.getSelection();
      const range = document.createRange();
      const node = document.createTextNode(displayText);
      const wrapper = document.createElement("div");
      wrapper.appendChild(node);
      document.body.appendChild(wrapper);
      range.selectNodeContents(wrapper);
      selection?.removeAllRanges();
      selection?.addRange(range);
      document.execCommand("copy");
      document.body.removeChild(wrapper);
      selection?.removeAllRanges();
      toast.success("已复制到剪贴板");
    } catch {
      toast.danger("复制失败，请手动选中文案后 Ctrl+C（Mac：Cmd+C）复制");
    }
  }, [displayText]);

  const isLoading =
    resetPasswordMutation.isPending ||
    (!!target?.peopleId && !target?.userId && personQuery.isLoading);
  const isError = resetPasswordMutation.isError;
  const noUser =
    target?.peopleId &&
    !personQuery.isLoading &&
    personQuery.data &&
    !(personQuery.data as { user?: { id: string } }).user?.id;

  const errMessage =
    resetPasswordMutation.error != null
      ? handleError(resetPasswordMutation.error).message || "重置失败，请稍后重试。"
      : "重置失败，请稍后重试。";

  return (
    <Modal isOpen={open} onOpenChange={handleOpenChange}>
      <RacDialogTriggerAnchor />
      <Modal.Backdrop>
        <Modal.Container placement="center" scroll="inside" size="md">
          <Modal.Dialog className="max-w-md">
            <Modal.Header className="gap-1">
              <Modal.Heading className="text-xl font-medium">重置密码</Modal.Heading>
              <Description className="sr-only">使用固定新密码重置，成功后展示并复制告知文案</Description>
            </Modal.Header>
            <Modal.Body className="gap-4 p-4">
              {isLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Spinner className="size-8" />
                </div>
              ) : null}
              {isError ? <p className="text-danger text-sm">{errMessage}</p> : null}
              {noUser ? (
                <p className="text-muted-foreground text-sm">该人员未关联用户，无法重置密码。</p>
              ) : null}
              {!isLoading && displayText ? (
                <p className="text-muted-foreground select-text break-all text-sm whitespace-pre-wrap">
                  {displayText}
                </p>
              ) : null}
            </Modal.Body>
            {!isLoading && displayText ? (
              <Modal.Footer>
                <Button onPress={handleCopy}>复制文案</Button>
              </Modal.Footer>
            ) : null}
          </Modal.Dialog>
        </Modal.Container>
      </Modal.Backdrop>
    </Modal>
  );
}
